import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";

type NavItemState = { main: string; contrastText: string };

// Paints a translucent state over whatever background the item already has.
const overlay = (colour: string) => `linear-gradient(${colour}, ${colour})`;

/**
 * A shell nav item (an app in the icon rail, or a nav-panel row) painted from
 * `palette.frame.navItem`. Each state is a `{ main, contrastText }` pair:
 * `main` paints the item, `contrastText` inks its text and icons alike.
 *
 * Hover, focus and pressed lie over the item's own background rather than
 * replacing it. A selected item keeps `selected.contrastText` under all of
 * them and takes no hover.
 *
 * Pass `selected` when the caller tracks selection itself (the rail).
 * Without it, a NavLink's `.active` class marks the selected row.
 */
export const frameNavItemSx =
  (selected?: boolean) =>
  (theme: Theme): SystemStyleObject<Theme> => {
    const { navItem } = theme.palette.frame;

    const paint = (base: NavItemState, isSelected: boolean) => {
      const ink = (state: NavItemState) =>
        isSelected ? base.contrastText : state.contrastText;
      return {
        backgroundColor: base.main,
        backgroundImage: "none",
        color: base.contrastText,
        "&:hover": isSelected
          ? { backgroundColor: base.main, backgroundImage: "none" }
          : {
              backgroundColor: base.main,
              backgroundImage: overlay(navItem.hover.main),
              color: ink(navItem.hover),
            },
        "&.Mui-focusVisible": {
          backgroundColor: base.main,
          backgroundImage: overlay(navItem.focus.main),
          color: ink(navItem.focus),
        },
        "&:active": {
          backgroundColor: base.main,
          backgroundImage: overlay(navItem.pressed.main),
          color: ink(navItem.pressed),
        },
      };
    };

    return {
      ...paint(navItem, false),
      ...(selected === true && paint(navItem.selected, true)),
      ...(selected === undefined && {
        "&.active": paint(navItem.selected, true),
      }),
      "&.Mui-disabled": {
        // No opacity: the disabled pair carries its own alpha.
        opacity: 1,
        backgroundColor: navItem.disabled.main,
        backgroundImage: "none",
        color: navItem.disabled.contrastText,
      },
      "& .MuiListItemIcon-root": { color: "inherit" },
    };
  };
